import { useState } from "react";
import { toast } from "sonner";
import { Reveal } from "./Reveal";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Label } from "./ui/label";
import { createEnquiry } from "../lib/api";
import { Phone, Mail, MapPin, MessageCircle } from "lucide-react";

export const Contact = ({ onBook }) => {
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "" });
  const [loading, setLoading] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.message.trim()) {
      toast.error("Please fill in your name, email and message.");
      return;
    }
    setLoading(true);
    try {
      await createEnquiry(form);
      toast.success("Thank you! We'll get back to you soon.");
      setForm({ name: "", email: "", phone: "", message: "" });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not send. Try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section
      id="contact"
      data-testid="contact"
      className="py-24 lg:py-32 bg-brand-cream"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-10 grid lg:grid-cols-2 gap-14">
        <Reveal>
          <p className="text-xs font-semibold tracking-[0.2em] text-brand uppercase mb-5">
            Talk to us
          </p>
          <h2 className="font-serif font-light text-4xl sm:text-5xl lg:text-6xl text-brand-ink leading-[1] tracking-tight mb-8">
            Let's care for them,
            <span className="italic text-brand"> together</span>.
          </h2>
          <p className="text-brand-ink/70 leading-relaxed max-w-md mb-10">
            Have a question before you book? Send us a note, or start a visit
            request directly. Our Jaipur team responds within a few hours.
          </p>

          <div className="space-y-5">
            <ContactRow icon={Phone} label="Phone" value="Coming soon" />
            <ContactRow icon={MessageCircle} label="WhatsApp" value="Coming soon" />
            <ContactRow icon={Mail} label="Email" value="cceldercare24@gmail.com" />
            <ContactRow icon={MapPin} label="Based in" value="Jaipur, Rajasthan" />
          </div>

          <Button
            data-testid="contact-book-btn"
            onClick={onBook}
            className="mt-10 rounded-full bg-brand hover:bg-brand-hover text-white px-8 h-13 py-3"
          >
            Request a home visit
          </Button>
        </Reveal>

        <Reveal delay={0.1}>
          <form
            onSubmit={submit}
            data-testid="enquiry-form"
            className="rounded-3xl bg-white border border-border p-8 space-y-5"
          >
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <Label className="text-brand-ink/80 text-sm mb-1.5 block">Name</Label>
                <Input
                  data-testid="enquiry-name"
                  value={form.name}
                  onChange={(e) => set("name", e.target.value)}
                  placeholder="Your name"
                />
              </div>
              <div>
                <Label className="text-brand-ink/80 text-sm mb-1.5 block">Phone</Label>
                <Input
                  data-testid="enquiry-phone"
                  value={form.phone}
                  onChange={(e) => set("phone", e.target.value)}
                  placeholder="+91 ..."
                />
              </div>
            </div>
            <div>
              <Label className="text-brand-ink/80 text-sm mb-1.5 block">Email</Label>
              <Input
                data-testid="enquiry-email"
                type="email"
                value={form.email}
                onChange={(e) => set("email", e.target.value)}
                placeholder="you@email.com"
              />
            </div>
            <div>
              <Label className="text-brand-ink/80 text-sm mb-1.5 block">Message</Label>
              <Textarea
                data-testid="enquiry-message"
                value={form.message}
                onChange={(e) => set("message", e.target.value)}
                placeholder="Tell us how we can help your parents..."
                rows={5}
              />
            </div>
            <Button
              data-testid="enquiry-submit"
              type="submit"
              disabled={loading}
              className="w-full rounded-full bg-brand hover:bg-brand-hover text-white h-12"
            >
              {loading ? "Sending..." : "Send enquiry"}
            </Button>
          </form>
        </Reveal>
      </div>
    </section>
  );
};

const ContactRow = ({ icon: Icon, label, value }) => (
  <div className="flex items-center gap-4">
    <span className="h-11 w-11 rounded-full bg-brand-sand flex items-center justify-center shrink-0">
      <Icon size={18} className="text-brand" />
    </span>
    <div>
      <p className="text-xs text-brand-ink/50 uppercase tracking-wide">{label}</p>
      <p className="text-brand-ink font-medium">{value}</p>
    </div>
  </div>
);
